import React from 'react'
import { Box, Card, Typography, Button, Divider, Stack, Tooltip } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useAppTranslation } from 'src/admin/core/hooks'
import { IssuedTickets } from '../models'
import { StatusLabel } from './StatusLabel'
import { formatAsCurrency, isWagerMultiRace } from 'src/admin/core'
import { DisplayLegs } from 'src/admin/core/components/DisplayLegs'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { formatToShortDate } from 'src/admin/core/services/dateFunctions'
import { useTheme } from '@mui/material/styles'
import { FinalTransaction } from './FinalTransaction'

interface TicketReceiptProps {
  ticket: IssuedTickets
  ticketNumber: string
  canCancel: boolean
  canPaid: boolean
}

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.spacing(1),
  boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
}))

const TicketInfo = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  width: '100%',
}))

const TicketRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  marginBottom: theme.spacing(0.5),
}))

const StyledDivider = styled(Divider)(({ theme }) => ({
  width: '100%',
  margin: `${theme.spacing(1)} 0`,
}))

const TicketReceipt: React.FC<TicketReceiptProps> = ({ ticket, ticketNumber, canCancel, canPaid }) => {
  const theme = useTheme()
  const { label } = useAppTranslation()
  const [copyVoidCode, setCopyVoidCode] = React.useState(false)
  const [copyPaidCode, setCopyPaidCode] = React.useState(false)

  const wagerCollection = ticket.horseRacesWagerCollections.length > 0 ? ticket.horseRacesWagerCollections[0] : null
  const total = ticket.totalAmount + ticket.surcharge

  return (
    <StyledCard>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <StatusLabel status={ticket.status} />
        <Stack direction="row" spacing={1}>
          {canCancel && (
            <Tooltip
              PopperProps={{
                disablePortal: true,
              }}
              onClose={() => setCopyVoidCode(false)}
              open={copyVoidCode}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              title={label('COPY_TICKET_AUDIT') as string}
            >
              <Button color="secondary" variant="outlined" size="small" disabled={ticket.status !== 'ISSUED'}>
                <CopyToClipboard
                  text={ticket.providerTicketNumbers.length > 0 ? ticket.providerTicketNumbers[0] : ''}
                  onCopy={() => {
                    setCopyVoidCode(true)
                    setTimeout(() => {
                      setCopyVoidCode(false)
                    }, 2000)
                  }}
                >
                  <span key="copy-number-text">{label('COPIAR_NUMERO_ANULAR') || 'Copiar Número'}</span>
                </CopyToClipboard>
              </Button>
            </Tooltip>
          )}

          {canPaid && (
            <Tooltip
              PopperProps={{
                disablePortal: true,
              }}
              onClose={() => setCopyPaidCode(false)}
              open={copyPaidCode}
              disableFocusListener
              disableHoverListener
              disableTouchListener
              title={label('COPY_TICKET_AUDIT') as string}
            >
              <Button color="secondary" variant="outlined" size="small" disabled={ticket.status !== 'ISSUED'}>
                <CopyToClipboard
                  text={ticket.number}
                  onCopy={() => {
                    setCopyPaidCode(true)
                    setTimeout(() => {
                      setCopyPaidCode(false)
                    }, 2000)
                  }}
                >
                  <span key="copy-number-text">{label('COPIAR_NUMERO_PAGO') || 'Copiar Número'}</span>
                </CopyToClipboard>
              </Button>
            </Tooltip>
          )}
        </Stack>
      </Box>
      <FinalTransaction ticket={ticket} />

      <StyledDivider />

      {/*       {
        wagerCollection && wagerCollection.event && wagerCollection.event.eventStatus !== 'Open' ?
        <Box>
          <Typography variant="body2">{label('TICKET_STATUS')} {ticket?.status} Carrera iniciada</Typography>
        </Box>
        : */}
      <React.Fragment>
        <TicketInfo>
          <TicketRow>
            <Typography variant="body2">{ticket?.workstation.enterprise.name || 'Consorcio No encontrado'}</Typography>
          </TicketRow>
          <TicketRow>
            <Typography variant="body2">{`${ticket?.workstation.agency.products[0].code} ${ticket?.workstation.agency.name}`}</Typography>
          </TicketRow>
          <TicketRow>
            <Typography variant="body2">{ticket?.workstation.name}</Typography>
          </TicketRow>
          <TicketRow>
            <Typography variant="body2">
              {formatToShortDate(ticket?.date, 'DD/MM/YYYY hh:mm:ss a')}
            </Typography>
          </TicketRow>
          <TicketRow>
            <Typography variant="body2">{wagerCollection ? wagerCollection.event.league.name : 'N/A'}</Typography>
          </TicketRow>
        </TicketInfo>

        <Box display="flex" justifyContent="center" mb={1}>
          <Typography variant="h6" fontWeight="bold">
            {`${ticketNumber}-****`}
          </Typography>
        </Box>

        <Box mb={1}>
          <Stack direction="row" spacing={1}>
          <Typography variant="subtitle1" fontWeight="bold">
            {`${label('CARRERA')} ${wagerCollection ? wagerCollection.race : 'N/A'} del ${formatToShortDate(wagerCollection ? wagerCollection.postTime : undefined)}`}
          </Typography>
          </Stack>
          <Typography variant="body2">{wagerCollection ? wagerCollection.event.league.name : 'N/A'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {wagerCollection ? wagerCollection.providerTicketId : 'N/A'}
          </Typography>
        </Box>

        <Box mb={1}>
          {wagerCollection &&
            wagerCollection?.wagers &&
            wagerCollection.wagers.length > 0 &&
            wagerCollection.wagers.map((wager) => {
              const isMulti = isWagerMultiRace(wager.poolId)
              return (
                <Stack
                  direction={'row'}
                  key={wager.id}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%' }}
                >
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {label(wager.poolId?.toString() ?? 'WIN')}
                    </Typography>
                    <Typography>
                      <DisplayLegs legs={wager.legs} isMulti={isMulti} />
                    </Typography>
                  </Box>
                  <Typography variant="body2" fontWeight="bold">
                    {formatAsCurrency(wager.total)}
                  </Typography>
                </Stack>
              )
            })}
        </Box>


        {
          ticket.status !== 'PAID' && (
            <>
              <StyledDivider />
              <Box display="flex" justifyContent="flex-end" alignItems="center" mb={1}>
                <Typography variant="h6" fontWeight="bold" color="text.success">
                  {`${label('PAGAR')} ${formatAsCurrency(wagerCollection?.wagerPayoff?.payoff?.net || 0.0)}`}
                </Typography>
              </Box>
            </>
          )
        }

        <StyledDivider />

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2">Surcharge</Typography>
          <Typography variant="h6" fontWeight="bold">
            {`${formatAsCurrency(ticket.surcharge)}`}
          </Typography>
        </Box>

        <StyledDivider />

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2">Surcharge</Typography>
          <Typography variant="h6" fontWeight="bold">
            {`${formatAsCurrency(ticket.surcharge)}`}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2">Total</Typography>
          <Typography variant="h6" fontWeight="bold">
            {`${formatAsCurrency(total)}`}
          </Typography>
        </Box>

        {
          ticket.status === 'PAID' && (
            <>
              <StyledDivider />
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle2" color={
                theme.palette.success.main
              }>{`${label('PAID_OFF')} (${ticket.currency || 'DOP'})`}</Typography>
              <Typography variant="h6" fontWeight="bold" color={theme.palette.success.main}>
                {`${formatAsCurrency(wagerCollection?.wagerPayoff?.payoff?.net || 0.0)}`}
              </Typography>
            </Box>
            </>
          )
        }
      </React.Fragment>
    </StyledCard>
  )
}

export default TicketReceipt
